import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  ChangeDetectionStrategy
} from '@angular/core';
import { DatePipe } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import {
  Student,
  School,
  NutritionType,
  Gender,
  PAKTKLASSE_FORM_VALUE,
  resolveGradeSelection,
  toGradeFormValue,
  isPaktklasseAllowedForSchool,
  getCurrentSchoolYear,
} from '@awo-care/shared-core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  ageAtSchoolYearStart,
  expectedBirthYear as calcExpectedBirthYear,
  isBirthDateImplausible,
  MAX_STUDENT_AGE_YEARS,
} from './student-birth-date.utils';
import { Timestamp } from 'firebase/firestore';

@Component({
  selector: 'lib-student-info-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatRadioModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatSelectModule,
    MatButtonModule,
    DatePipe,
  ],
  template: `
    <div class="student-form">
      @if (!readOnly() && !editMode()) {
      <div class="step-description" i18n="@@student-info-form.step-description">
        Bitte geben Sie hier die persönlichen Daten Ihres Kindes ein. Diese
        Informationen benötigen wir für die Vergabe des Betreuungsplatzes.
      </div>
      }

      <form [formGroup]="form" class="form-content">
        <!-- Personal Information Section -->
        <div class="section personal-info">
          <div class="section-header">
            <h3 i18n="@@student-info-form.personal-data">Persönliche Daten</h3>
            @if (canEdit() && readOnly() && !editMode()) {
            <button
              mat-button
              type="button"
              (click)="toggleEditMode()"
              class="edit-button"
            >
              <mat-icon>edit</mat-icon>
              <span i18n="@@common.edit">Bearbeiten</span>
            </button>
            } @if (editMode()) {
            <div class="edit-actions">
              <button
                mat-button
                type="button"
                (click)="cancelEdit()"
                i18n="@@common.cancel"
              >
                Abbrechen
              </button>
              <button
                mat-raised-button
                color="primary"
                type="button"
                (click)="saveChanges()"
                [disabled]="!form.valid || !form.dirty"
                i18n="@@common.save"
              >
                Speichern
              </button>
            </div>
            }
          </div>

          @if (showEditDisabledHint() && editMode()) {
          <div class="edit-disabled-hint">
            <mat-icon>info</mat-icon>
            <span>{{ editDisabledHintText() }}</span>
          </div>
          }

          <mat-form-field appearance="outline">
            <mat-label i18n="@@student-info-form.first-name">Vorname</mat-label>
            <input
              matInput
              formControlName="firstName"
              [readonly]="
                (readOnly() && !editMode()) ||
                (showEditDisabledHint() && editMode())
              "
              required
              data-testid="student-firstname"
            />
            @if (form.get('firstName')?.hasError('required') &&
            form.get('firstName')?.touched) {
            <mat-error i18n="@@student-info-form.first-name-required"
              >Vorname ist erforderlich</mat-error
            >
            }
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label i18n="@@student-info-form.last-name">Nachname</mat-label>
            <input
              matInput
              formControlName="lastName"
              [readonly]="
                (readOnly() && !editMode()) ||
                (showEditDisabledHint() && editMode())
              "
              required
              data-testid="student-lastname"
            />
            @if (form.get('lastName')?.hasError('required') &&
            form.get('lastName')?.touched) {
            <mat-error i18n="@@student-info-form.last-name-required"
              >Nachname ist erforderlich</mat-error
            >
            }
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label i18n="@@student-info-form.birth-date"
              >Geburtsdatum</mat-label
            >
            <input
              matInput
              [matDatepicker]="picker"
              [min]="birthDateMin"
              [max]="birthDateMax"
              formControlName="birthDate"
              [readonly]="
                (readOnly() && !editMode()) ||
                (showEditDisabledHint() && editMode())
              "
              required
              placeholder="DD.MM.YYYY"
            />
            <mat-datepicker-toggle
              matIconSuffix
              [for]="picker"
            ></mat-datepicker-toggle>
            <mat-datepicker
              #picker
              startView="multi-year"
              [startAt]="birthDateStartAt()"
            ></mat-datepicker>
            @if (birthDateSummary(); as summary) {
            <mat-hint>
              {{ summary.date | date: 'fullDate' }}
              <span i18n="@@student-info-form.birth-date-age-hint"
                >&mdash; bei Schuljahresbeginn {{ summary.age }} Jahre
                alt</span
              >
            </mat-hint>
            } @else {
            <mat-hint>DD.MM.YYYY</mat-hint>
            } @if (form.get('birthDate')?.hasError('required') &&
            form.get('birthDate')?.touched) {
            <mat-error i18n="@@student-info-form.birth-date-required"
              >Geburtsdatum ist erforderlich</mat-error
            >
            } @if (form.get('birthDate')?.hasError('matDatepickerParse')) {
            <mat-error i18n="@@student-info-form.birth-date-invalid"
              >Bitte ein gültiges Datum eingeben</mat-error
            >
            } @if (form.get('birthDate')?.hasError('matDatepickerMin') ||
            form.get('birthDate')?.hasError('matDatepickerMax')) {
            <mat-error i18n="@@student-info-form.birth-date-out-of-range"
              >Das Geburtsdatum liegt außerhalb des plausiblen
              Bereichs</mat-error
            >
            }
          </mat-form-field>

          @if (birthDateImplausible()) {
          <div class="birth-date-plausibility" data-testid="birth-date-warning">
            <mat-icon>warning_amber</mat-icon>
            <div>
              <p i18n="@@student-info-form.birth-date-implausible">
                Das Geburtsdatum passt nicht zur angegebenen Klasse — für diesen
                Jahrgang wäre etwa {{ expectedBirthYear() }} zu erwarten. Bitte
                noch einmal prüfen.
              </p>
              @if (!softValidation()) {
              <mat-checkbox
                [checked]="birthDateConfirmed()"
                (change)="onBirthDateConfirmedChange($event.checked)"
                data-testid="birth-date-confirm"
                i18n="@@student-info-form.birth-date-confirm"
                >Das Geburtsdatum ist trotzdem korrekt</mat-checkbox
              >
              }
            </div>
          </div>
          }

          <mat-form-field appearance="outline">
            <mat-label i18n="@@student-info-form.gender">Geschlecht</mat-label>
            <mat-select
              formControlName="gender"
              required
              data-testid="student-gender"
            >
              <mat-option value="male" i18n="@@student-info-form.gender-male"
                >Männlich</mat-option
              >
              <mat-option value="female" i18n="@@student-info-form.gender-female"
                >Weiblich</mat-option
              >
              <mat-option value="diverse" i18n="@@student-info-form.gender-diverse"
                >Divers</mat-option
              >
            </mat-select>
            @if (form.get('gender')?.hasError('required') &&
            form.get('gender')?.touched) {
            <mat-error i18n="@@student-info-form.gender-required"
              >Geschlecht ist erforderlich</mat-error
            >
            }
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label i18n="@@student-info-form.starting-grade"
              >Klasse des Kindes im <strong>kommenden</strong>
              Schuljahr</mat-label
            >
            <mat-select
              formControlName="startingGrade"
              required
              data-testid="student-starting-grade"
            >
              <mat-option [value]="0" i18n="@@student-info-form.grade-vorklasse"
                >Vorklasse</mat-option
              >
              <mat-option [value]="1" i18n="@@student-info-form.grade-1"
                >1. Klasse</mat-option
              >
              <mat-option [value]="2" i18n="@@student-info-form.grade-2"
                >2. Klasse</mat-option
              >
              <mat-option [value]="3" i18n="@@student-info-form.grade-3"
                >3. Klasse</mat-option
              >
              <mat-option [value]="4" i18n="@@student-info-form.grade-4"
                >4. Klasse</mat-option
              >
              @if (paktklasseVisible()) {
              <mat-option
                [value]="PAKTKLASSE_FORM_VALUE"
                i18n="@@student-info-form.grade-paktklasse"
                >Paktklasse</mat-option
              >
              }
            </mat-select>
            @if (form.get('startingGrade')?.hasError('required') &&
            form.get('startingGrade')?.touched) {
            <mat-error i18n="@@student-info-form.starting-grade-required"
              >Klasse ist erforderlich</mat-error
            >
            }
          </mat-form-field>
        </div>

        <!-- Health Information Section -->
        <div class="section health-info" formGroupName="healthInfo">
          <h3 i18n="@@student-info-form.health-information">
            Gesundheitsinformationen
          </h3>

          <div class="form-group">
            <div class="radio-group">
              <div class="label" i18n="@@student-info-form.allergies">
                Allergien*
              </div>
              <mat-radio-group
                formControlName="allergies"
                data-testid="student-allergies"
              >
                <mat-radio-button [value]="true" i18n="@@common.yes"
                  >Ja</mat-radio-button
                >
                <mat-radio-button [value]="false" i18n="@@common.no"
                  >Nein</mat-radio-button
                >
              </mat-radio-group>
              @if (form.get('healthInfo.allergies')?.touched && form.get('healthInfo.allergies')?.hasError('required')) {
                <mat-error i18n="@@common.please-select">Bitte auswählen</mat-error>
              }
            </div>

            @if (form.get('healthInfo.allergies')?.value) {
            <mat-form-field>
              <mat-label i18n="@@student-info-form.describe-allergies"
                >Bitte beschreiben Sie die Allergien</mat-label
              >
              <textarea
                matInput
                formControlName="allergiesDescription"
                rows="3"
                [readonly]="readOnly() && !editMode()"
              ></textarea>
            </mat-form-field>

            <div class="radio-group">
              <div
                class="label"
                i18n="@@student-info-form.emergency-medication"
              >
                Bekommt das Kind Notfallmedikamente (z.B. Notfallset,
                Adrenalin-Autoinjektor)?
              </div>
              <mat-radio-group
                formControlName="emergencyMedication"
                data-testid="student-emergency-medication"
              >
                <mat-radio-button [value]="true" i18n="@@common.yes"
                  >Ja</mat-radio-button
                >
                <mat-radio-button [value]="false" i18n="@@common.no"
                  >Nein</mat-radio-button
                >
              </mat-radio-group>
            </div>

            @if (form.get('healthInfo.emergencyMedication')?.value) {
            <div class="emergency-medication-hint" role="note">
              <mat-icon aria-hidden="true">description</mat-icon>
              <div class="emergency-medication-hint__body">
                <p
                  class="emergency-medication-hint__title"
                  i18n="@@student-info-form.emergency-medication-hint-title"
                >
                  Zwei Formulare müssen vorgelegt werden
                </p>
                <p i18n="@@student-info-form.emergency-medication-hint-text">
                  Bitte beide Formulare ausdrucken, ausfüllen, vom Arzt
                  unterschreiben lassen und zeitnah in der Einrichtung abgeben.
                  Die Anmeldung kann auch ohne die Unterlagen abgeschlossen
                  werden.
                </p>
                <ul class="emergency-medication-hint__links">
                  <li>
                    <a
                      href="shared-assets/documents/3-050_UKH_Formular_Notfallplan-Anaphylaxie-Schule.pdf"
                      target="_blank"
                      rel="noopener"
                      data-testid="emergency-medication-pdf-ukh"
                    >
                      <span
                        i18n="@@student-info-form.emergency-medication-pdf-ukh"
                        >Notfallplan Anaphylaxie (Unfallkasse Hessen)</span
                      >
                    </a>
                  </li>
                  <li>
                    <a
                      href="shared-assets/documents/DAAB_Ermaechtigungsbescheinigung.pdf"
                      target="_blank"
                      rel="noopener"
                      data-testid="emergency-medication-pdf-daab"
                    >
                      <span
                        i18n="@@student-info-form.emergency-medication-pdf-daab"
                        >Ermächtigungsbescheinigung (Deutscher Allergie- und
                        Asthmabund)</span
                      >
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            }
            }
          </div>

          <div class="form-group">
            <div class="radio-group">
              <div class="label" i18n="@@student-info-form.chronic-diseases">
                Dauerhafte Krankheiten/Einschränkungen*
              </div>
              <mat-radio-group
                formControlName="chronicDiseases"
                data-testid="student-chronic-diseases"
              >
                <mat-radio-button [value]="true" i18n="@@common.yes"
                  >Ja</mat-radio-button
                >
                <mat-radio-button [value]="false" i18n="@@common.no"
                  >Nein</mat-radio-button
                >
              </mat-radio-group>
              @if (form.get('healthInfo.chronicDiseases')?.touched && form.get('healthInfo.chronicDiseases')?.hasError('required')) {
                <mat-error i18n="@@common.please-select">Bitte auswählen</mat-error>
              }
            </div>

            @if (form.get('healthInfo.chronicDiseases')?.value) {
            <mat-form-field>
              <mat-label i18n="@@student-info-form.describe-chronic-diseases"
                >Beschreibung der Krankheiten/Einschränkungen</mat-label
              >
              <textarea
                matInput
                formControlName="chronicDiseasesDescription"
                rows="3"
                [readonly]="readOnly() && !editMode()"
              ></textarea>
            </mat-form-field>
            }
          </div>

          <div class="info-note" i18n="@@student-info-form.vaccinationInfo">
            Vollständiger Masernimpfschutz (2-fach) ist Voraussetzung für die
            Teilnahme.
          </div>

          <div class="form-group">
            <div class="radio-group">
              <div
                class="label"
                i18n="@@student-info-form.measles-vaccination-available"
              >
                Masernimpfung vorhanden
              </div>
              <mat-radio-group
                formControlName="measlesVaccination"
                data-testid="student-measles-vaccination"
              >
                <mat-radio-button [value]="true" i18n="@@common.yes"
                  >Ja</mat-radio-button
                >
                <mat-radio-button [value]="false" i18n="@@common.no"
                  >Nein</mat-radio-button
                >
              </mat-radio-group>
              @if (form.get('healthInfo.measlesVaccination')?.touched && form.get('healthInfo.measlesVaccination')?.hasError('required')) {
                <mat-error i18n="@@common.please-select">Bitte auswählen</mat-error>
              }
            </div>

            @if (form.get('healthInfo.measlesVaccination')?.value === false) {
            <div class="consent-section">
              <p
                class="info-text"
                i18n="@@student-info-form.health-office-certificate-required"
              >
                Eine Aufnahme ist nur nach Vorlage einer
                Unbedenklichkeitsbescheinigung des Gesundheitsamts möglich.
              </p>
              <mat-checkbox
                formControlName="measlesVaccinationConsent"
                i18n="@@student-info-form.notice-understood"
              >
                Ich habe den Hinweis gelesen und verstanden
              </mat-checkbox>
            </div>
            }
          </div>

          <!-- Tetanus Vaccination Date -->
          <div class="form-group">
            <div
              class="label"
              i18n="@@student-info-form.tetanus-vaccination-heading"
            >
              Tetanusimpfung
            </div>
            @if (!form.get('healthInfo.tetanusVaccinationNotPossible')?.value) {
            <mat-form-field appearance="outline">
              <mat-label
                i18n="@@student-info-form.tetanus-vaccination-date"
                >Datum der letzten Tetanusimpfung</mat-label
              >
              <input
                matInput
                [matDatepicker]="tetanusPicker"
                formControlName="tetanusVaccinationDate"
                [readonly]="readOnly() && !editMode()"
                [required]="!softValidation()"
                placeholder="DD.MM.YYYY"
                data-testid="student-tetanus-vaccination-date"
              />
              <mat-datepicker-toggle
                matIconSuffix
                [for]="tetanusPicker"
              ></mat-datepicker-toggle>
              <mat-datepicker #tetanusPicker></mat-datepicker>
              @if (softValidation() && editMode() && !form.get('healthInfo.tetanusVaccinationDate')?.value) {
              <mat-hint class="warn-hint"
                i18n="@@student-info-form.tetanus-vaccination-date-missing"
                >Tetanusimpfung-Datum fehlt noch</mat-hint
              >
              } @else {
              <mat-hint>DD.MM.YYYY</mat-hint>
              }
              @if (form.get('healthInfo.tetanusVaccinationDate')?.hasError('required') &&
              form.get('healthInfo.tetanusVaccinationDate')?.touched) {
              <mat-error
                i18n="@@student-info-form.tetanus-vaccination-date-required"
                >Datum der letzten Tetanusimpfung ist erforderlich</mat-error
              >
              }
            </mat-form-field>
            }

            <!-- Kontraindikation: Impfung nicht möglich -->
            <mat-checkbox
              formControlName="tetanusVaccinationNotPossible"
              data-testid="student-tetanus-not-possible"
              i18n="@@student-info-form.tetanus-not-possible"
            >
              Impfung nicht möglich (Kontraindikation)
            </mat-checkbox>

            @if (form.get('healthInfo.tetanusVaccinationNotPossible')?.value) {
            <mat-checkbox
              formControlName="tetanusVaccinationDoctorCertificate"
              data-testid="student-tetanus-doctor-certificate"
              i18n="@@student-info-form.tetanus-doctor-certificate"
            >
              Ärztliche Bescheinigung liegt vor
            </mat-checkbox>
            }
          </div>

          <!-- Nutrition Section -->
          <div class="form-group">
            <div class="label" i18n="@@student-info-form.nutrition">
              Ernährung*
            </div>
            <mat-form-field appearance="outline">
              <mat-label i18n="@@student-info-form.nutrition-form"
                >Ernährungsform</mat-label
              >
              <mat-select
                formControlName="nutrition"
                required
                data-testid="student-nutrition"
              >
                <mat-option
                  value="normal"
                  i18n="@@student-info-form.no-restrictions"
                  >Keine Einschränkungen</mat-option
                >
                <mat-option value="no_pork" i18n="@@student-info-form.no-pork"
                  >Kein Schweinefleisch</mat-option
                >
                <mat-option
                  value="vegetarian"
                  i18n="@@student-info-form.vegetarian"
                  >Vegetarisch</mat-option
                >
                <mat-option value="vegan" i18n="@@student-info-form.vegan"
                  >Vegan</mat-option
                >
                <mat-option value="other" i18n="@@student-info-form.other"
                  >Sonstiges</mat-option
                >
              </mat-select>
              @if (form.get('healthInfo.nutrition')?.hasError('required') &&
              form.get('healthInfo.nutrition')?.touched) {
              <mat-error i18n="@@student-info-form.nutrition-form-required"
                >Ernährungsform ist erforderlich</mat-error
              >
              }
            </mat-form-field>

            @if (form.get('healthInfo.nutrition')?.value === 'other') {
            <mat-form-field appearance="outline">
              <mat-label i18n="@@student-info-form.please-specify"
                >Bitte spezifizieren</mat-label
              >
              <textarea
                matInput
                formControlName="nutritionOther"
                rows="2"
                placeholder="z.B. Laktoseintoleranz, Erdnussallergie"
                [readonly]="readOnly() && !editMode()"
              ></textarea>
              <mat-hint
                i18n="@@student-info-form.special-nutrition-requirements"
                >Bitte geben Sie spezielle Ernährungsanforderungen an</mat-hint
              >
              @if (form.get('healthInfo.nutritionOther')?.hasError('required')
              && form.get('healthInfo.nutritionOther')?.touched) {
              <mat-error i18n="@@student-info-form.description-required"
                >Beschreibung ist erforderlich</mat-error
              >
              }
            </mat-form-field>
            }
          </div>

          <div class="info-note" i18n="@@student-info-form.health-info-notice">
            *Die Erziehungsberechtigten sind verpflichtet Allergien, besonderer
            Betreuungs- oder Förderungsbedarfe (auch durch Teilhabe- Assistenten
            unterstützt) Ihres Kindes im Vorfeld mitzuteilen.
          </div>
        </div>
      </form>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .step-description {
        color: #666;
        line-height: 1.5;
        margin-bottom: 2rem;
        font-size: 0.95rem;
        background-color: #f5f5f5;
        padding: 1rem;
        border-radius: 4px;
        border-left: 4px solid var(--theme-accent);
      }

      .form-content {
        display: grid;
        gap: 2rem;
        /* Touch-Target der rechtsbündigen Buttons ragt ~4px über den Rand und
           löste in mat-tab-body (overflow: auto) eine horizontale Scrollbar aus (#1347) */
        overflow-x: clip;
      }

      .section {
        display: grid;
        gap: 1rem;
      }

      .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .section h3,
      .section-header h3 {
        margin: 0 0 1rem;
        color: rgba(0, 0, 0, 0.87);
        font-size: 1.2rem;
        font-weight: 500;
      }

      mat-form-field {
        width: 100%;
      }

      .form-group {
        display: grid;
        gap: 1rem;
      }

      .radio-group {
        display: grid;
        gap: 0.5rem;
      }

      .label {
        font-size: 1rem;
        color: rgba(0, 0, 0, 0.87);
      }

      mat-radio-group {
        display: flex;
        gap: 1rem;
      }

      .consent-section {
        display: grid;
        gap: 1rem;
        padding: 1rem;
        background-color: #f5f5f5;
        border-radius: 4px;
      }

      .emergency-medication-hint {
        display: flex;
        gap: 0.75rem;
        padding: 1rem;
        border: 1px solid #f57c00;
        border-left-width: 4px;
        border-radius: 4px;
        background-color: #fff8e1;
        color: rgba(0, 0, 0, 0.87);
      }

      .emergency-medication-hint mat-icon {
        color: #f57c00;
        flex-shrink: 0;
      }

      .emergency-medication-hint__body {
        display: grid;
        gap: 0.5rem;
      }

      .emergency-medication-hint__title {
        margin: 0;
        font-weight: 600;
      }

      .emergency-medication-hint p {
        margin: 0;
      }

      .emergency-medication-hint__links {
        margin: 0;
        padding-left: 1.25rem;
        display: grid;
        gap: 0.25rem;
      }

      .emergency-medication-hint__links a {
        color: inherit;
      }

      .info-text {
        margin: 0;
        color: rgba(0, 0, 0, 0.7);
      }

      .warn-hint {
        color: #f57c00;
      }

      .birth-date-plausibility {
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
        padding: 0.75rem 1rem;
        border-left: 3px solid #f57c00;
        border-radius: 4px;
        background: rgba(245, 124, 0, 0.08);
        color: rgba(0, 0, 0, 0.87);
      }

      .birth-date-plausibility mat-icon {
        flex-shrink: 0;
        color: #f57c00;
      }

      .birth-date-plausibility p {
        margin: 0 0 0.5rem;
        font-size: 0.875rem;
        line-height: 1.5;
      }

      .birth-date-plausibility p:last-child {
        margin-bottom: 0;
      }

      .info-note {
        color: #666;
        font-size: 0.875rem;
        line-height: 1.5;
        margin-top: 1rem;
        padding: 1rem;
        background-color: #f5f5f5;
        border-radius: 4px;
      }

      .edit-button {
        color: var(--theme-accent);
      }

      .edit-actions {
        display: flex;
        gap: 1rem;
      }

      .edit-disabled-hint {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: var(--mat-warn-600);
        font-size: 0.875rem;
        margin-bottom: 1rem;
        padding: 0.5rem 0;

        mat-icon {
          font-size: 1rem;
          width: 1rem;
          height: 1rem;
        }
      }
    `,
  ],
})
export class StudentInfoFormComponent {
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);

  data = input<Student | null>(null);
  readOnly = input<boolean>(false);
  /**
   * Blendet die Schaltflaeche „Bearbeiten" aus. Default `true` — nur Aufrufer,
   * die Schreibrechte pruefen, setzen das auf `false` (Teamleitung, #875).
   * Ersetzt keine Serverpruefung, die steht in `firestore.rules`.
   */
  canEdit = input<boolean>(true);
  validateTrigger = input(-1);
  stepIndex = input(-1);
  formControl = output<AbstractControl>();
  formSubmit = output<Partial<Student>>();

  // Track if we're waiting for an async save to complete
  private pendingSave = signal(false);

  editMode = input<boolean>(false);
  editModeChange = output<boolean>();

  // For customer-ui: show hint when editing is disabled for users
  showEditDisabledHint = input<boolean>(false);
  editDisabledHintText = input<string>('');

  // For employee-ui: soft validation — show warnings instead of blocking save
  softValidation = input<boolean>(false);

  /**
   * Harte Grenzen für das Geburtsdatum eines Schulkinds (#997, verschärft mit
   * #999). Sie kennen die Klasse nicht und blocken deshalb nur, was für *jedes*
   * Schulkind falsch ist; der jahrgangsgenaue Abgleich passiert eine Ebene
   * weiter über `isBirthDateImplausible`.
   *
   * Ohne Grenzen wanderte ein Zahlendreher wie `03.04.202` unbemerkt nach
   * Firestore und riss dort den Excel-Export auf.
   *
   * Die Untergrenze von 20 Jahren ist am ältesten denkbaren Schulkind
   * hergeleitet: Klasse 4 im frühesten wählbaren Schuljahr (dem Vorjahr), spät
   * eingeschult und zweimal wiederholt — rund 13 Jahre alt. Sechs Jahre Puffer
   * darüber, und trotzdem fällt jeder Erwachsenen-Jahrgang hart durch. Das war
   * mit den vorherigen 30 Jahren nicht der Fall: Wer das eigene Geburtsdatum
   * ins Feld des Kindes tippte, kam mit den Jahrgängen ab 1996 ungehindert
   * durch.
   *
   * `scripts/admin/find-implausible-birthdates.ts` spiegelt diesen Bereich —
   * beim Ändern dort nachziehen.
   */
  protected readonly birthDateMin = new Date(
    new Date().getFullYear() - MAX_STUDENT_AGE_YEARS,
    0,
    1
  );
  protected readonly birthDateMax = new Date();

  /**
   * Startpunkt des Kalenders — normalerweise `null`, damit der Datepicker wie
   * gewohnt auf dem gespeicherten Geburtsdatum aufgeht.
   *
   * Liegt der gespeicherte Wert außerhalb des plausiblen Bereichs, öffnete der
   * Kalender bisher im Jahr 202: In der Mehrjahres-Ansicht führte von dort kein
   * praktikabler Weg zurück ins 21. Jahrhundert, ein kaputtes Datum ließ sich
   * also gar nicht mehr per Kalender korrigieren. Dann startet er stattdessen
   * bei einem typischen Einschulungsjahrgang.
   */
  protected readonly birthDateStartAt = computed<Date | null>(() => {
    const stored = this.data()?.birthDate;
    const date =
      stored instanceof Timestamp ? stored.toDate() : (stored ?? null);
    if (date && date >= this.birthDateMin && date <= this.birthDateMax) {
      return null;
    }
    return new Date(this.birthDateMax.getFullYear() - 8, 0, 1);
  });

  /**
   * Schuljahr, für das das Kind angemeldet wird — der Anker der
   * Plausibilitätsprüfung (#999). Die Neu-Anmeldung reicht das im Stepper
   * gewählte Jahr durch; beim Bearbeiten steht es am Kind selbst. Bleibt beides
   * leer, ist das laufende Schuljahr die beste verfügbare Annahme.
   */
  schoolYear = input<number | null>(null);

  protected readonly effectiveSchoolYear = computed(
    () =>
      this.schoolYear() ??
      this.data()?.yearInfo?.year ??
      getCurrentSchoolYear().year
  );

  /**
   * Gespiegelte Formularwerte. Hinweis und Warnung hängen an Signalen statt an
   * `form.get(...)`, damit sie unabhängig von der Change-Detection-Strategie
   * der einbindenden App aktualisieren.
   */
  private readonly birthDateValue = signal<Date | null>(null);
  private readonly gradeValue = signal<number | null>(null);

  /**
   * Vom Nutzer bewusst bestätigter Ausreisser. Absichtlich kein Formularfeld —
   * der Wert ist reines UI-Gate und hat im gespeicherten Kind nichts verloren.
   */
  protected readonly birthDateConfirmed = signal(false);

  protected readonly birthDateImplausible = computed(() =>
    isBirthDateImplausible(
      this.birthDateValue(),
      this.effectiveSchoolYear(),
      this.gradeValue()
    )
  );

  /** Erwarteter Jahrgang — erscheint im Warntext als Orientierung. */
  protected readonly expectedBirthYear = computed(() =>
    calcExpectedBirthYear(this.effectiveSchoolYear(), this.gradeValue() ?? 1)
  );

  /**
   * Hinweiszeile unter dem Feld: ausgeschriebenes Datum plus Alter zum
   * Schuljahresbeginn. Fängt die Zahlendreher ab, die innerhalb der Toleranz
   * liegen und deshalb keine Warnung auslösen.
   */
  protected readonly birthDateSummary = computed(() => {
    const date = this.birthDateValue();
    const age = ageAtSchoolYearStart(date, this.effectiveSchoolYear());
    return date && age !== null ? { date, age } : null;
  });

  /**
   * Blockiert den Anmeldeschritt, solange ein unplausibles Geburtsdatum nicht
   * bestätigt ist. Im Mitarbeiter-Portal (`softValidation`) bleibt es bei der
   * reinen Warnung — dort sitzt jemand, der den Fall einschätzen kann.
   */
  private readonly birthDatePlausibilityValidator = (
    _control: AbstractControl
  ): ValidationErrors | null =>
    this.birthDateImplausible() &&
    !this.birthDateConfirmed() &&
    !this.softValidation()
      ? { birthDateImplausible: true }
      : null;

  // Für das Template (Paktklasse-Sentinel im Dropdown)
  protected readonly PAKTKLASSE_FORM_VALUE = PAKTKLASSE_FORM_VALUE;

  // Bei Neu-Anmeldung ist `data` null und die gewählte Schule kommt über
  // `currentSchool` aus dem Store (wie bei parent-info-form). Beim Bearbeiten
  // ist `currentSchool` null und der Landkreis steckt in data.startInfo.school.
  currentSchool = input<School | null>(null);

  // Paktklasse nur anbieten, wenn die Schule sie freigeschaltet hat
  // (`offersPaktklasse`) — oder das Kind bereits eine Paktklasse ist, damit
  // die Auswahl beim Bearbeiten nicht verschwindet.
  protected readonly paktklasseVisible = computed(() => {
    const student = this.data();
    const school = this.currentSchool() ?? student?.startInfo?.school;
    return (
      student?.classType === 'paktklasse' || isPaktklasseAllowedForSchool(school)
    );
  });

  form = this.fb.group({
    firstName: ['', [Validators.required]],
    lastName: ['', [Validators.required]],
    birthDate: [
      null as Date | null,
      [Validators.required, this.birthDatePlausibilityValidator],
    ],
    gender: [null as Gender | null, [Validators.required]],
    startingGrade: [
      // Hält den Dropdown-Wert: 0 = Vorklasse, 1..4 = Klasse,
      // PAKTKLASSE_FORM_VALUE (-1) = Paktklasse. Wird beim Speichern via
      // resolveGradeSelection() in startingGrade + classType aufgelöst.
      null as number | null,
      [
        Validators.required,
        Validators.min(PAKTKLASSE_FORM_VALUE),
        Validators.max(4),
      ],
    ],
    healthInfo: this.fb.group({
      allergies: [null as boolean | null, [Validators.required]],
      allergiesDescription: [''],
      // Optional (#914) — die Anmeldung soll ohne die Angabe abschließbar bleiben
      emergencyMedication: [null as boolean | null],
      chronicDiseases: [null as boolean | null, [Validators.required]],
      chronicDiseasesDescription: [''],
      measlesVaccination: [null as boolean | null, [Validators.required]],
      measlesVaccinationConsent: [false],
      tetanusVaccinationDate: [null as Date | null],
      tetanusVaccinationNotPossible: [false],
      tetanusVaccinationDoctorCertificate: [false],
      nutrition: ['normal' as NutritionType, [Validators.required]],
      nutritionOther: [''],
    }),
  });

  private originalFormValue: any = null;

  constructor() {
    // Trigger validation display only when this step is targeted
    effect(() => {
      const trigger = this.validateTrigger();
      if (trigger >= 0 && trigger === this.stepIndex()) {
        this.form.markAllAsTouched();
      }
    });

    // Soft validation: remove required validator for new fields in employee-ui.
    // Tetanus-Datum ist außerdem nicht erforderlich, wenn eine Kontraindikation
    // vermerkt ist (Impfung nicht möglich).
    effect(() => {
      this.softValidation();
      this.applyTetanusDateValidator();
    });

    // Handle form disable/enable state
    effect(() => {
      const shouldDisable = this.readOnly() && !this.editMode();
      const shouldDisablePersonalDataOnly =
        this.showEditDisabledHint() && this.editMode();

      if (shouldDisable) {
        this.form.disable({ emitEvent: false });
      } else if (shouldDisablePersonalDataOnly) {
        // Only disable personal data fields, keep health info enabled
        this.form.get('firstName')?.disable({ emitEvent: false });
        this.form.get('lastName')?.disable({ emitEvent: false });
        this.form.get('birthDate')?.disable({ emitEvent: false });
        this.form.get('gender')?.disable({ emitEvent: false });
        this.form.get('startingGrade')?.disable({ emitEvent: false });
        this.form.get('healthInfo')?.enable({ emitEvent: false });
      } else {
        this.form.enable({ emitEvent: false });
      }
    });

    effect(() => {
      const student = this.data();
      if (student) {
        // Convert Timestamp to Date for the datepicker
        let birthDateValue: Date | null = null;
        if (student.birthDate) {
          birthDateValue =
            student.birthDate instanceof Timestamp
              ? student.birthDate.toDate()
              : student.birthDate;
        }

        this.form.patchValue(
          {
            firstName: student.firstName || '',
            lastName: student.lastName || '',
            birthDate: birthDateValue,
            gender: student.gender || null,
            startingGrade: toGradeFormValue(
              student.startingGrade,
              student.classType
            ),
            healthInfo: {
              allergies: student.healthInfo?.allergies ?? null,
              allergiesDescription:
                student.healthInfo?.allergiesDescription ?? '',
              emergencyMedication:
                student.healthInfo?.emergencyMedication ?? null,
              chronicDiseases: student.healthInfo?.chronicDiseases ?? null,
              chronicDiseasesDescription:
                student.healthInfo?.chronicDiseasesDescription ?? '',
              measlesVaccination:
                student.healthInfo?.measlesVaccination ?? null,
              measlesVaccinationConsent:
                student.healthInfo?.measlesVaccinationConsent ?? false,
              tetanusVaccinationDate:
                student.healthInfo?.tetanusVaccinationDate instanceof Timestamp
                  ? student.healthInfo.tetanusVaccinationDate.toDate()
                  : (student.healthInfo?.tetanusVaccinationDate ?? null),
              tetanusVaccinationNotPossible:
                student.healthInfo?.tetanusVaccinationNotPossible ?? false,
              tetanusVaccinationDoctorCertificate:
                student.healthInfo?.tetanusVaccinationDoctorCertificate ?? false,
              nutrition: student.healthInfo?.nutrition ?? 'normal',
              nutritionOther: student.healthInfo?.nutritionOther ?? '',
            },
          },
          { emitEvent: false }
        );

        // patchValue() oben läuft mit emitEvent: false, die valueChanges-
        // Subscriptions greifen hier also nicht.
        this.birthDateConfirmed.set(false);
        this.syncBirthDatePlausibility();

        // Store original values for cancel functionality - but preserve edit mode
        this.originalFormValue = this.form.getRawValue();

        // If we just saved (pendingSave is false but editMode is true), keep form pristine
        if (this.editMode() && !this.pendingSave()) {
          this.form.markAsPristine();
        }
      }
    });

    // Setup conditional validators
    this.setupConditionalValidators();

    // Geburtsdatum und Klasse in die Signale spiegeln. Eine geänderte Angabe
    // setzt eine frühere Bestätigung zurück — sonst bliebe der zweite Tippfehler
    // durch die Bestätigung des ersten gedeckt.
    for (const controlName of ['birthDate', 'startingGrade']) {
      this.form
        .get(controlName)
        ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.birthDateConfirmed.set(false);
          this.syncBirthDatePlausibility();
        });
    }

    // Der Validator hängt an Signalen, nicht am Formularwert — Angular muss
    // deshalb explizit neu validieren, wenn sich eines davon ändert.
    effect(() => {
      this.birthDateImplausible();
      this.birthDateConfirmed();
      this.softValidation();
      untracked(() =>
        this.form.get('birthDate')?.updateValueAndValidity({ emitEvent: false })
      );
    });

    this.form.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (!this.readOnly() && this.form.valid && this.form.dirty) {
          this.formControl.emit(this.form);
        }
      });
  }

  private syncBirthDatePlausibility(): void {
    const raw = this.form.getRawValue();
    this.birthDateValue.set(raw.birthDate ?? null);
    this.gradeValue.set(raw.startingGrade ?? null);
  }

  protected onBirthDateConfirmedChange(checked: boolean): void {
    this.birthDateConfirmed.set(checked);
  }

  toggleEditMode(): void {
    this.editModeChange.emit(true);
  }

  cancelEdit(): void {
    if (this.originalFormValue) {
      this.form.patchValue(this.originalFormValue, { emitEvent: false });
      this.form.markAsPristine();
      this.birthDateConfirmed.set(false);
      this.syncBirthDatePlausibility();
    }
    this.editModeChange.emit(false);
  }

  saveChanges(): void {
    if (this.form.valid && this.form.dirty) {
      const formValue = this.form.getRawValue();

      // Dropdown-Wert (inkl. Paktklasse-Sentinel) in grade + classType auflösen
      const gradeSelection =
        formValue.startingGrade == null
          ? undefined
          : resolveGradeSelection(formValue.startingGrade);

      // Convert and clean up the data for Student model
      const studentData: Partial<Student> = {
        firstName: formValue.firstName || undefined,
        lastName: formValue.lastName || undefined,
        birthDate: formValue.birthDate
          ? Timestamp.fromDate(formValue.birthDate)
          : undefined,
        gender: formValue.gender || undefined,
        startingGrade: gradeSelection?.grade,
        classType: gradeSelection?.classType,
        healthInfo: {
          allergies: formValue.healthInfo.allergies ?? false,
          allergiesDescription: formValue.healthInfo.allergiesDescription ?? '',
          emergencyMedication:
            formValue.healthInfo.emergencyMedication ?? false,
          chronicDiseases: formValue.healthInfo.chronicDiseases ?? false,
          chronicDiseasesDescription:
            formValue.healthInfo.chronicDiseasesDescription ?? '',
          measlesVaccination: formValue.healthInfo.measlesVaccination ?? false,
          measlesVaccinationConsent:
            formValue.healthInfo.measlesVaccinationConsent ?? false,
          tetanusVaccinationDate: formValue.healthInfo.tetanusVaccinationDate
            ? Timestamp.fromDate(formValue.healthInfo.tetanusVaccinationDate)
            : null,
          tetanusVaccinationNotPossible:
            formValue.healthInfo.tetanusVaccinationNotPossible ?? false,
          tetanusVaccinationDoctorCertificate:
            formValue.healthInfo.tetanusVaccinationDoctorCertificate ?? false,
          nutrition: formValue.healthInfo.nutrition ?? 'normal',
          nutritionOther: formValue.healthInfo.nutritionOther ?? '',
        },
      };

      // Mark that we're waiting for async save
      this.pendingSave.set(true);
      this.formSubmit.emit(studentData);

      // Don't exit edit mode - parent will decide via resetFormState() or confirmSaveComplete()
    }
  }

  // Method for parent to call when save is complete
  confirmSaveComplete(): void {
    if (this.pendingSave()) {
      this.originalFormValue = this.form.getRawValue();
      this.form.markAsPristine();
      this.pendingSave.set(false);
      this.editModeChange.emit(false);
    }
  }

  // Method to reset form state without closing edit mode (for modal behavior)
  resetFormState(): void {
    if (this.pendingSave()) {
      this.originalFormValue = this.form.getRawValue();
      this.form.markAsPristine();
      this.pendingSave.set(false);
      // Don't emit editModeChange - keep modal open
    }
  }

  // Method to mark form as saved without closing edit mode
  markAsSaved(): void {
    if (this.pendingSave()) {
      this.originalFormValue = this.form.getRawValue();
      this.form.markAsPristine();
      this.pendingSave.set(false);
      // Stay in edit mode - don't emit editModeChange
    }
  }

  /**
   * Setzt den Required-Validator des Tetanus-Datums abhängig von Soft-Validation
   * und Kontraindikation. Bei Kontraindikation ("Impfung nicht möglich") oder
   * aktiver Soft-Validation ist das Datum optional.
   */
  private applyTetanusDateValidator(): void {
    const dateControl = this.form.get('healthInfo.tetanusVaccinationDate');
    const notPossible = this.form.get(
      'healthInfo.tetanusVaccinationNotPossible'
    )?.value;
    if (this.softValidation() || notPossible) {
      dateControl?.clearValidators();
    } else {
      dateControl?.setValidators([Validators.required]);
    }
    dateControl?.updateValueAndValidity({ emitEvent: false });
  }

  private setupConditionalValidators(): void {
    // Kontraindikation: Datum leeren + Required entfernen, wenn "Impfung nicht
    // möglich" gesetzt wird. Bescheinigungs-Häkchen zurücksetzen, sobald die
    // Kontraindikation wieder abgewählt wird.
    this.form
      .get('healthInfo.tetanusVaccinationNotPossible')
      ?.valueChanges.pipe(
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((notPossible) => {
        if (notPossible) {
          this.form
            .get('healthInfo.tetanusVaccinationDate')
            ?.setValue(null, { emitEvent: false });
        } else {
          this.form
            .get('healthInfo.tetanusVaccinationDoctorCertificate')
            ?.setValue(false, { emitEvent: false });
        }
        this.applyTetanusDateValidator();
      });

    // Allergies description validator
    this.form
      .get('healthInfo.allergies')
      ?.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((hasAllergies) => {
        const descControl = this.form.get('healthInfo.allergiesDescription');
        if (hasAllergies) {
          descControl?.setValidators([Validators.required]);
        } else {
          descControl?.clearValidators();
          // Ohne Allergie ist die Notfallmedikamenten-Frage nicht mehr sichtbar —
          // sonst bliebe ein unsichtbares "Ja" stehen (#914).
          this.form
            .get('healthInfo.emergencyMedication')
            ?.setValue(null, { emitEvent: false });
        }
        descControl?.updateValueAndValidity();
      });

    // Chronic diseases description validator
    this.form
      .get('healthInfo.chronicDiseases')
      ?.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((hasChronicDiseases) => {
        const descControl = this.form.get(
          'healthInfo.chronicDiseasesDescription'
        );
        if (hasChronicDiseases) {
          descControl?.setValidators([Validators.required]);
        } else {
          descControl?.clearValidators();
        }
        descControl?.updateValueAndValidity();
      });

    // Measles vaccination consent validator
    this.form
      .get('healthInfo.measlesVaccination')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((hasMeaslesVaccination) => {
        const consentControl = this.form.get(
          'healthInfo.measlesVaccinationConsent'
        );
        if (hasMeaslesVaccination === false) {
          consentControl?.setValidators([Validators.requiredTrue]);
        } else {
          consentControl?.clearValidators();
        }
        consentControl?.updateValueAndValidity();
      });

    // Nutrition other field validator
    this.form
      .get('healthInfo.nutrition')
      ?.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((nutritionType) => {
        const otherControl = this.form.get('healthInfo.nutritionOther');
        if (nutritionType === 'other') {
          otherControl?.setValidators([Validators.required]);
        } else {
          otherControl?.clearValidators();
        }
        otherControl?.updateValueAndValidity();
      });
  }
}
